import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { settingsApi } from '../services/api';
import { taskKeys } from '../lib/queryKeys';
import { useViewing } from '../lib/viewing';

const TaskContext = createContext(null);

export const useTask = () => {
  const ctx = useContext(TaskContext);
  if (!ctx) throw new Error('useTask must be used within TaskProvider');
  return ctx;
};

// For components that also render outside the app (an answer in a test or a public page):
// the task popup when there is one, else null.
export const useOptionalTask = () => useContext(TaskContext);

export const TaskProvider = ({ children }) => {
  // Creation state
  const [isCreationOpen, setIsCreationOpen] = useState(false);
  const [creationPrefills, setCreationPrefills] = useState(null);

  // Detail state
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [activeTaskId, setActiveTaskId] = useState(null);
  useViewing(isDetailOpen && activeTaskId ? `task:${activeTaskId}` : null);  // no ping about the open task
  const [detailTeamId, setDetailTeamId] = useState(null);

  // View mode preference
  const [taskDetailViewMode, setTaskDetailViewModeState] = useState(
    () => localStorage.getItem('task_detail_view_mode') || 'sidebar'
  );

  // URL param sync
  const navigate = useNavigate();
  const location = useLocation();
  // Read when the detail opens or closes, so the functions (and the context value) stay
  // the same across navigations: every useTask() caller, in every kept tab, drew again.
  // (The router's navigate changes with the path too.)
  const locationRef = useRef(location);
  locationRef.current = location;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const qc = useQueryClient();

  // Load preferences from backend on mount - only for authenticated users.
  // Public landing pages (/, /features/*, /pricing, etc.) must not call /api/settings,
  // because the api.js 401 interceptor would redirect them to /login.
  useEffect(() => {
    if (!localStorage.getItem('token')) return;
    settingsApi.get()
      .then(res => {
        const prefs = res.data?.ui_preferences || {};
        const mode = prefs.task_detail_view_mode;
        if (mode && (mode === 'sidebar' || mode === 'popup')) {
          setTaskDetailViewModeState(mode);
          localStorage.setItem('task_detail_view_mode', mode);
        }
      })
      .catch(() => {});
  }, []);

  // Open detail from URL param on mount / URL change (only the URL triggers it: closing the
  // detail must not open it again before the URL loses its ?task=)
  // A link to another task while one is open (a tapped notification) opens that one.
  const isDetailOpenRef = useRef(isDetailOpen);
  isDetailOpenRef.current = isDetailOpen;
  const activeTaskIdRef = useRef(activeTaskId);
  activeTaskIdRef.current = activeTaskId;
  // A link from a notification e-mail can also say what to do (?do=complete | reply,
  // backend routes/notifications.notification_buttons): the open task does it once, and the
  // URL loses it, so a reload or Back does not do it again.
  const [taskAction, setTaskAction] = useState(null);  // { taskId, action }
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const taskParam = params.get('task');
    if (taskParam && (!isDetailOpenRef.current || taskParam !== activeTaskIdRef.current)) {
      setActiveTaskId(taskParam);
      setIsDetailOpen(true);
    }
    const action = params.get('do');
    if (taskParam && (action === 'complete' || action === 'reply')) {
      setTaskAction({ taskId: taskParam, action });
      params.delete('do');
      navigateRef.current(`${location.pathname}?${params.toString()}`, { replace: true });
    }
  }, [location.search]); // eslint-disable-line
  const takeTaskAction = useCallback((taskId) => {
    if (!taskAction || taskAction.taskId !== taskId) return null;
    setTaskAction(null);
    return taskAction.action;
  }, [taskAction]);

  const openTaskCreation = useCallback((prefills = null) => {
    // Close detail if open
    setIsDetailOpen(false);
    setActiveTaskId(null);
    setCreationPrefills(prefills);
    setIsCreationOpen(true);
  }, []);

  const closeTaskCreation = useCallback(() => {
    setIsCreationOpen(false);
    setCreationPrefills(null);
  }, []);

  const openTaskDetail = useCallback((taskId, options = {}) => {
    // Close creation if open
    setIsCreationOpen(false);
    setCreationPrefills(null);
    setActiveTaskId(taskId);
    setDetailTeamId(options.teamId || null);
    setIsDetailOpen(true);
    // Update URL param. Use the router location (basename-stripped) rather than
    // window.location: when the app is served under a /<lang> basename (e.g.
    // busybee.day/en/app), window.location.pathname still includes that prefix,
    // and navigate() re-applies the basename — producing /en/en/app, which
    // matches no route and 404s. location.pathname has the basename removed, so
    // navigate() adds it back exactly once.
    const { pathname, search } = locationRef.current;
    const params = new URLSearchParams(search);
    params.set('task', taskId);
    navigateRef.current(`${pathname}?${params.toString()}`, { replace: true });
  }, []);

  // Listen for legacy openTaskPanel events (backward compat). With the current
  // openTaskDetail: the first one kept the page the app was opened on.
  useEffect(() => {
    const handler = (e) => {
      const taskId = e.detail?.taskId || e.detail;
      if (taskId) {
        openTaskDetail(taskId, { teamId: e.detail?.teamId });
      }
    };
    window.addEventListener('openTaskPanel', handler);
    return () => window.removeEventListener('openTaskPanel', handler);
  }, [openTaskDetail]);

  const closeTaskDetail = useCallback(() => {
    setIsDetailOpen(false);
    setActiveTaskId(null);
    setDetailTeamId(null);
    // Remove URL param. Same basename caveat as openTaskDetail: use the
    // router location (basename-stripped), not window.location.
    const { pathname, search } = locationRef.current;
    const params = new URLSearchParams(search);
    params.delete('task');
    const qs = params.toString();
    navigateRef.current(`${pathname}${qs ? `?${qs}` : ''}`, { replace: true });
  }, []);

  const setTaskDetailViewMode = useCallback((mode) => {
    setTaskDetailViewModeState(mode);
    localStorage.setItem('task_detail_view_mode', mode);
    settingsApi.update({ 'ui_preferences.task_detail_view_mode': mode }).catch(() => {});
  }, []);

  const notifyTaskMutated = useCallback(() => {
    qc.invalidateQueries({ queryKey: taskKeys.all });
  }, [qc]);

  const value = useMemo(() => ({
    isCreationOpen,
    creationPrefills,
    openTaskCreation,
    closeTaskCreation,
    isDetailOpen,
    activeTaskId,
    detailTeamId,
    openTaskDetail,
    closeTaskDetail,
    taskDetailViewMode,
    setTaskDetailViewMode,
    notifyTaskMutated,
    taskAction,
    takeTaskAction,
  }), [isCreationOpen, creationPrefills, openTaskCreation, closeTaskCreation, isDetailOpen, activeTaskId,
    detailTeamId, openTaskDetail, closeTaskDetail, taskDetailViewMode, setTaskDetailViewMode, notifyTaskMutated,
    taskAction, takeTaskAction]);

  return (
    <TaskContext.Provider value={value}>
      {children}
    </TaskContext.Provider>
  );
};
